import { useCallback, useMemo, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import api from '../utils/api';
import {
  AUTO_PLAN_MAX_ROUTES,
  getOrderId,
  isValidLatLng,
} from '../constants/routePlanning';

const ESTIMATE_DEBOUNCE_MS = 500;
export const UNASSIGNED = 'unassigned';

/**
 * Multi-route state for the map's Auto-plan mode: owns N routes plus a pool of unassigned orders,
 * and drives one POST /delivery-routes per route on create. Deliberately separate from
 * useRouteBuilder (single-route Manual mode) so Manual mode and the checkbox flow cannot regress.
 *
 * Route shape: { id, colorIndex, orderedIds, totalDistanceMeters, totalDurationSeconds,
 *                sequenceSource, estimating, estimateError }
 */
export default function useMultiRouteBuilder({ onMessage } = {}) {
  const [ordersById, setOrdersById] = useState({});
  const [missingLocationOrders, setMissingLocationOrders] = useState([]);
  const [returnToStore, setReturnToStoreState] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [unassignedIds, setUnassignedIds] = useState([]);
  const [originStoreId, setOriginStoreId] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdRoutes, setCreatedRoutes] = useState([]);

  const routeIdCounterRef = useRef(0);
  const estimateTimersRef = useRef({});

  const notify = useCallback(
    (message, severity = 'info') => {
      if (typeof onMessage === 'function') onMessage(message, severity);
    },
    [onMessage]
  );

  const reset = useCallback(() => {
    Object.values(estimateTimersRef.current).forEach((t) => clearTimeout(t));
    estimateTimersRef.current = {};
    routeIdCounterRef.current = 0;
    setOrdersById({});
    setMissingLocationOrders([]);
    setReturnToStoreState(false);
    setRoutes([]);
    setUnassignedIds([]);
    setOriginStoreId(null);
    setPlanning(false);
    setPlanError('');
    setCreating(false);
    setCreatedRoutes([]);
  }, []);

  /** Seed the order pool from full order objects already in hand (map fetch). */
  const initFromOrders = useCallback((orders = []) => {
    const byId = {};
    const missing = [];
    orders.forEach((order) => {
      const id = getOrderId(order);
      if (!id) return;
      byId[id] = order;
      if (!isValidLatLng(order.deliveryAddress?.latLong)) missing.push(order);
    });
    Object.values(estimateTimersRef.current).forEach((t) => clearTimeout(t));
    estimateTimersRef.current = {};
    routeIdCounterRef.current = 0;
    setOrdersById(byId);
    setMissingLocationOrders(missing);
    setRoutes([]);
    setUnassignedIds([]);
    setOriginStoreId(null);
    setPlanError('');
    setCreatedRoutes([]);
  }, []);

  const setReturnToStore = useCallback((value) => {
    setReturnToStoreState(Boolean(value));
  }, []);

  const patchRoute = useCallback((routeId, patch) => {
    setRoutes((prev) =>
      prev.map((route) =>
        route.id === routeId
          ? { ...route, ...(typeof patch === 'function' ? patch(route) : patch) }
          : route
      )
    );
  }, []);

  const runEstimate = useCallback(
    async (routeId, ids, shouldReturn) => {
      if (!ids.length) {
        patchRoute(routeId, {
          totalDistanceMeters: 0,
          totalDurationSeconds: 0,
          estimating: false,
          estimateError: '',
        });
        return;
      }
      patchRoute(routeId, { estimating: true, estimateError: '' });
      try {
        const response = await api.post('/route-planning/estimate', {
          orderIds: ids,
          returnToStore: shouldReturn,
        });
        const data = response.data;
        if (data.success) {
          patchRoute(routeId, {
            totalDistanceMeters: data.totalDistanceMeters,
            totalDurationSeconds: data.totalDurationSeconds,
            estimating: false,
            estimateError: '',
            sequenceSource: 'manual',
          });
        } else {
          patchRoute(routeId, { estimating: false, estimateError: data.message || 'Estimate failed' });
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Estimate failed';
        patchRoute(routeId, { estimating: false, estimateError: message });
      }
    },
    [patchRoute]
  );

  /** Debounced per-route estimate — not one request per drag frame. */
  const scheduleEstimate = useCallback(
    (routeId, ids, shouldReturn) => {
      if (estimateTimersRef.current[routeId]) {
        clearTimeout(estimateTimersRef.current[routeId]);
      }
      estimateTimersRef.current[routeId] = setTimeout(() => {
        runEstimate(routeId, ids, shouldReturn);
      }, ESTIMATE_DEBOUNCE_MS);
    },
    [runEstimate]
  );

  const autoPlan = useCallback(
    async ({ routeCount, maxStopsPerRoute, maxDurationMinutes }) => {
      const orderIds = Object.keys(ordersById).filter((id) =>
        isValidLatLng(ordersById[id]?.deliveryAddress?.latLong)
      );
      if (orderIds.length === 0) {
        setPlanError('No orders with a valid location to plan.');
        return false;
      }

      setPlanning(true);
      setPlanError('');
      try {
        const response = await api.post('/route-planning/auto-plan', {
          orderIds,
          routeCount,
          maxStopsPerRoute: maxStopsPerRoute || undefined,
          maxDurationMinutes: maxDurationMinutes || undefined,
          returnToStore,
        });
        const data = response.data;
        if (!data.success) {
          setPlanError(data.message || 'Auto plan failed');
          return false;
        }

        const planned = (data.routes || []).map((route) => {
          const id = ++routeIdCounterRef.current;
          return {
            id,
            colorIndex: id - 1,
            orderedIds: route.orderedOrderIds || [],
            totalDistanceMeters: route.totalDistanceMeters ?? null,
            totalDurationSeconds: route.totalDurationSeconds ?? null,
            sequenceSource: 'auto',
            estimating: false,
            estimateError: '',
          };
        });

        setRoutes(planned);
        setUnassignedIds(data.unassignedOrderIds || []);
        setOriginStoreId(data.originStoreId || null);
        setCreatedRoutes([]);

        if ((data.unassignedOrderIds || []).length > 0) {
          notify(
            `${data.unassignedOrderIds.length} order(s) could not fit the limits and are left unassigned.`,
            'warning'
          );
        } else {
          notify(`Planned ${planned.length} route(s).`, 'success');
        }
        return true;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Auto plan failed';
        setPlanError(message);
        notify(message, 'error');
        return false;
      } finally {
        setPlanning(false);
      }
    },
    [ordersById, returnToStore, notify]
  );

  /** Move an order between routes (or to/from the unassigned pool). */
  const moveOrder = useCallback(
    (orderId, toRouteId) => {
      setRoutes((prevRoutes) => {
        let affected = [];
        const stripped = prevRoutes.map((route) => {
          if (route.orderedIds.includes(orderId)) {
            affected.push(route.id);
            return { ...route, orderedIds: route.orderedIds.filter((id) => id !== orderId) };
          }
          return route;
        });

        const next = stripped.map((route) => {
          if (route.id === toRouteId && !route.orderedIds.includes(orderId)) {
            affected.push(route.id);
            const orderedIds = [...route.orderedIds, orderId];
            return {
              ...route,
              orderedIds,
              sequenceSource: 'manual',
            };
          }
          return route;
        });

        // Schedule estimates for every route that changed.
        [...new Set(affected)].forEach((rid) => {
          const r = next.find((route) => route.id === rid);
          if (r) scheduleEstimate(rid, r.orderedIds, returnToStore);
        });
        return next;
      });

      setUnassignedIds((prev) => {
        const withoutOrder = prev.filter((id) => id !== orderId);
        return toRouteId === UNASSIGNED ? [...withoutOrder, orderId] : withoutOrder;
      });
    },
    [returnToStore, scheduleEstimate]
  );

  const reorderWithin = useCallback(
    (routeId, activeId, overId) => {
      if (!overId || activeId === overId) return;
      setRoutes((prev) =>
        prev.map((route) => {
          if (route.id !== routeId) return route;
          const oldIndex = route.orderedIds.indexOf(activeId);
          const newIndex = route.orderedIds.indexOf(overId);
          if (oldIndex < 0 || newIndex < 0) return route;
          const orderedIds = arrayMove(route.orderedIds, oldIndex, newIndex);
          scheduleEstimate(routeId, orderedIds, returnToStore);
          return { ...route, orderedIds, sequenceSource: 'manual' };
        })
      );
    },
    [returnToStore, scheduleEstimate]
  );

  /** Create one delivery route per planned route. Partial failures are reported, not fatal. */
  const createAll = useCallback(async () => {
    const toCreate = routes.filter((r) => r.orderedIds.length > 0);
    if (toCreate.length === 0) {
      notify('No routes to create.', 'warning');
      return { created: [], failed: [] };
    }

    setCreating(true);
    const created = [];
    const failed = [];
    try {
      for (const route of toCreate) {
        try {
          const response = await api.post('/delivery-routes', { orderIds: route.orderedIds });
          const data = response.data;
          if (data.success && data.route) {
            created.push({
              localId: route.id,
              colorIndex: route.colorIndex,
              routeId: data.route.routeId,
              orderedIds: route.orderedIds,
              totalDistanceMeters: route.totalDistanceMeters,
              totalDurationSeconds: route.totalDurationSeconds,
            });
          } else {
            failed.push({ localId: route.id, message: data.message || 'Create failed' });
          }
        } catch (error) {
          failed.push({
            localId: route.id,
            message: error.response?.data?.message || error.message || 'Create failed',
          });
        }
      }

      setCreatedRoutes(created);
      // Drop successfully created routes from the editable set so a retry only re-sends failures.
      const createdIds = new Set(created.map((c) => c.localId));
      setRoutes((prev) => prev.filter((r) => !createdIds.has(r.id)));

      if (failed.length === 0) {
        notify(`Created ${created.length} route(s).`, 'success');
      } else {
        notify(`Created ${created.length}, failed ${failed.length}. Retry the failed routes.`, 'warning');
      }
      return { created, failed };
    } finally {
      setCreating(false);
    }
  }, [routes, notify]);

  const routedIds = useMemo(() => {
    const set = new Set();
    routes.forEach((route) => route.orderedIds.forEach((id) => set.add(id)));
    return set;
  }, [routes]);

  const routeOfOrder = useCallback(
    (orderId) => {
      const found = routes.find((route) => route.orderedIds.includes(orderId));
      return found ? found.id : null;
    },
    [routes]
  );

  const sequenceIndex = useCallback(
    (orderId) => {
      for (const route of routes) {
        const idx = route.orderedIds.indexOf(orderId);
        if (idx >= 0) return idx + 1;
      }
      return null;
    },
    [routes]
  );

  const totals = useMemo(() => {
    let distance = 0;
    let routed = 0;
    routes.forEach((route) => {
      if (typeof route.totalDistanceMeters === 'number') distance += route.totalDistanceMeters;
      routed += route.orderedIds.length;
    });
    return { distance, routed, routeCount: routes.length };
  }, [routes]);

  return {
    ordersById,
    missingLocationOrders,
    returnToStore,
    setReturnToStore,
    routes,
    unassignedIds,
    originStoreId,
    planning,
    planError,
    creating,
    createdRoutes,
    totals,
    routedIds,
    maxRoutes: AUTO_PLAN_MAX_ROUTES,
    initFromOrders,
    reset,
    autoPlan,
    moveOrder,
    reorderWithin,
    createAll,
    routeOfOrder,
    sequenceIndex,
  };
}
